export class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.anchor = this.querySelector("a");
    if (!this.anchor) return;

    this.isLoading = false;
    // Trigger loading when element is this many pixels away from entering viewport
    // Increase this value to load even earlier
    this.threshold = 5000;

    this.checkPosition = this.checkPosition.bind(this);

    // Check on scroll/touch events - more efficient than RAF loop
    window.addEventListener("scroll", this.checkPosition, { passive: true });
    window.addEventListener("touchmove", this.checkPosition, { passive: true });
    window.addEventListener("resize", this.checkPosition, { passive: true });
    
    // Initial check
    this.checkPosition();
  }

  // A filter refresh replaces the grid; a detached instance would otherwise load its stale page into the new one
  disconnectedCallback() {
    window.removeEventListener("scroll", this.checkPosition);
    window.removeEventListener("touchmove", this.checkPosition);
    window.removeEventListener("resize", this.checkPosition);
  }

  checkPosition() {
    if (this.isLoading || !this.anchor) return;

    // Get element's position relative to viewport
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Load when element is within (viewportHeight + threshold) pixels from top of viewport
    if (rect.top < viewportHeight + this.threshold) {
      this.loadNextPage();
    }
  }

  async loadNextPage() {
    this.isLoading = true;
    window.removeEventListener("scroll", this.checkPosition);
    window.removeEventListener("touchmove", this.checkPosition);

    this.anchor.style.display = "flex";
    this.anchor.innerText = "Loading...";

    const href = this.anchor.getAttribute("href");
    if (!href) return;

    const nextPageUrl = new URL(href, window.location.origin);
    const nextParams = new URLSearchParams(nextPageUrl.search);
    const pageValue = nextParams.get("page");
    const sectionIdValue = nextParams.get("section_id");

    if (pageValue) nextParams.set("page", pageValue);
    if (sectionIdValue) nextParams.set("section_id", sectionIdValue);

    nextPageUrl.search = nextParams.toString();

    try {
      const response = await fetch(nextPageUrl.toString());
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");
      if (!this.isConnected) return;

      // Grab the product grid from the matching collection-info scope
      const newGrid = html.querySelector("collection-info [data-product-grid]");
      const collectionInfo = this.closest("collection-info");
      const grid = collectionInfo ? collectionInfo.querySelector("[data-product-grid]") : document.querySelector("[data-product-grid]");

      const isEmptyStateGrid = newGrid && newGrid.children.length === 1
        && newGrid.firstElementChild?.tagName === "P";

      if (newGrid && grid && !isEmptyStateGrid) {
        // Use insertAdjacentHTML for faster DOM insertion (single reflow)
        grid.insertAdjacentHTML("beforeend", newGrid.innerHTML);
      }

      // Handle next infinite scroll
      const newInfinite = html.querySelector("collection-info infinite-scroll");
      const hasValidAnchor = newInfinite && newInfinite.querySelector("a[href]");
      
      if (hasValidAnchor && !isEmptyStateGrid) {
        this.replaceWith(newInfinite);
      } else {
        this.remove();
      }
    } catch (err) {
      console.error("InfiniteScroll error:", err);
      this.isLoading = false;
      window.addEventListener("scroll", this.checkPosition, { passive: true });
      window.addEventListener("touchmove", this.checkPosition, { passive: true });
    }

    this.anchor.style.display = "none";
    this.anchor.innerText = "";
  }
}

if (!customElements.get("infinite-scroll")) {
  customElements.define("infinite-scroll", InfiniteScroll);
}